import { Divider } from '@mui/material';
import { ActionStatus } from 'further-types/lp/action';
import Button from 'components/Button';
import ErrorBoundary from 'components/ErrorBoundary';
import AllocationsManager, {
  Allocation,
} from 'components/new/AllocationsManager';
import FullHeightView from 'components/new/FullHeightView';
import { AllocationRule } from 'further-types/lp/allocations';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import { investment as rules } from 'further-ui/validations';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import useHoldingClasses from '../hooks/useHoldingClasses';
import BaseInvestmentForm from './BaseInvestmentForm';
import HoldingClassInfoForm from './HoldingClassInfoForm';

type CompleteAllocationProps = {
  onSuccess: () => void;
  onCancel: () => void;
};

const CompleteAllocation: React.FC<CompleteAllocationProps> = ({
  onSuccess,
  onCancel,
}) => {
  const notification = useNotification();
  const apiUtils = api.useUtils();
  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    setError,
    formState,
  } = useFormContext<rules.CreateInvestmentFormValues>();

  const vehicleId = getValues('vehicleId');
  const { vehicle } = useVehicle(vehicleId);

  const {
    fields,
    calculateTotalInvestmentAmount,
    getCurrency,
    validateHoldingClassAmounts,
    hasNewHoldingClass,
  } = useHoldingClasses({
    control,
    watch,
    setValue,
    getValues,
    setError,
  });

  const totalInvestmentAmountTcy = calculateTotalInvestmentAmount();
  const currency = getCurrency();

  const upsertInvestmentMutation = api.lpInvestment.upsert.useMutation({
    onSuccess: () => {
      apiUtils.lpInvestment.invalidate();
      apiUtils.holdingClass.invalidate();
      apiUtils.asset.invalidate();
      apiUtils.generalLedger.invalidate();
      notification.success('Draft saved successfully.');
    },
    onError: (error) => {
      notification.error(error.message || 'Failed to save draft.');
    },
  });

  const validateDealCompletionDate = () => {
    const dealCompletionDate = getValues('dealCompletionDate');
    if (!dealCompletionDate) {
      setError('dealCompletionDate', {
        message: 'Deal completion date is required.',
      });
      return false;
    }
    return true;
  };
  const onSubmit = async (data: any) => {
    const isDealCompletionDateValid = validateDealCompletionDate();
    const isHoldingClassAmountsValid = validateHoldingClassAmounts();

    if (!isDealCompletionDateValid || !isHoldingClassAmountsValid) {
      return;
    }

    if (data.status !== ActionStatus.Approved) {
      await onSaveDraft(data);
    }
    onSuccess();
  };

  const onSaveDraft = handleSubmit(async (data) => {
    const isDealCompletionDateValid = validateDealCompletionDate();
    const isHoldingClassAmountsValid = validateHoldingClassAmounts();
    if (!isDealCompletionDateValid || !isHoldingClassAmountsValid) {
      return;
    }

    const isNewAsset = getValues('isNewAsset') && !getValues('assetId');

    if (getValues('createdNewAsset') === undefined || isNewAsset) {
      setValue('createdNewAsset', isNewAsset);
    }

    if (
      getValues('createdNewHoldingClass') === undefined ||
      hasNewHoldingClass()
    ) {
      setValue('createdNewHoldingClass', hasNewHoldingClass());
    }

    const response = await upsertInvestmentMutation.mutateAsync({
      ...data,
      status: ActionStatus.Draft,
    });

    const { id, assetId, holdingClasses, transactions } = response;

    setValue('investmentId', id);
    setValue('assetId', assetId);
    setValue('isNewAsset', false);
    setValue(
      'holdingClasses',
      holdingClasses.map((holdingClass) => ({
        holdingClassId: holdingClass.holdingClassId.toString(),
        isNewHoldingClass: false as const,
        name: holdingClass.name,
        investmentAmountTcy: holdingClass.investmentAmountTcy,
        numberOfUnits: holdingClass.numberOfUnits,
        unitPriceTcy: holdingClass.unitPriceTcy,
        currency: holdingClass.currency,
      })),
    );
    setValue('transactions', transactions);
  });

  const allocationRule = watch('allocationRule');
  const allocations = watch('allocations');
  const fundingSource = watch('fundingSource');
  const status = watch('status');

  const handleAllocationRuleChange = (allocationRule: AllocationRule) => {
    setValue('allocationRule', allocationRule);
  };

  const handleAllocationsChange = (allocations: Array<Allocation>) => {
    setValue('allocations', allocations);
  };

  const investmentId = watch('investmentId');
  const dealCompletionDate = watch('dealCompletionDate');

  return (
    <FullHeightView
      actionBar={
        <Row justify="space-between" spacing="sm" centered>
          <Button variant="outlined" onClick={onCancel} size="large">
            Previous step
          </Button>
          <Row centered spacing="sm">
            <Button
              variant="outlined"
              loading={upsertInvestmentMutation.isPending}
              onClick={onSaveDraft}
              size="large"
            >
              Save draft
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit, () => {
                notification.error('Please fill in all required fields.');
              })}
              loading={upsertInvestmentMutation.isPending}
              size="large"
            >
              {status === ActionStatus.Approved ? 'Continue' : 'Save and next'}
            </Button>
          </Row>
        </Row>
      }
    >
      <form>
        <Column spacing="lg">
          <BaseInvestmentForm vehicleId={vehicleId} />
          <Divider />
          {fields.map((_, index) => (
            <HoldingClassInfoForm key={index} index={index} />
          ))}

          {!!dealCompletionDate && (
            <ErrorBoundary>
              <AllocationsManager
                vehicleId={getValues('vehicleId')}
                allocatedActionLabel="Investment"
                currency={currency}
                baseCurrency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
                allocationRule={allocationRule}
                allocations={allocations ?? []}
                allocatedAmountTcy={totalInvestmentAmountTcy}
                error={formState.errors.allocations?.message}
                onAllocationRuleChange={handleAllocationRuleChange}
                onAllocationsChange={handleAllocationsChange}
                useGeneralCapital={
                  fundingSource === FundingSource.GeneralCapitalOnAccounts
                }
                currentActionIds={investmentId ? [investmentId] : undefined}
                date={dealCompletionDate}
              />
            </ErrorBoundary>
          )}
        </Column>
      </form>
    </FullHeightView>
  );
};

export default CompleteAllocation;
